import { useState, useRef, useEffect, useMemo } from "react";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import LoginService from "../../service/LoginService";
import { Dialog } from "primereact/dialog";
import "./Login.css";
import ReCaptchaButton from "../../components/ReCaptchaButton/ReCaptchaButton";
import { Link } from "react-router-dom";
import localeText from "../../locale";
import OrderService from "../../service/OrderService";
import SEO from "../../components/SEO/SEO";
import { useLocation, useHistory } from "react-router-dom";
import { Checkbox } from "primereact/checkbox";
import ThemeService from "../../service/ThemeService";
import { resolveThemeImageUrl } from "../../utilities/resolveThemeImageUrl";

const Login = () => {
  const location = useLocation();
  const history = useHistory();
  const from = location.state?.from;
  const usenameRefferal = location?.state?.username;

  const [rememberMe, setRememberMe] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [tokenRecaptcha, setTokenRecaptcha] = useState("");
  const [shareToken, setShareToken] = useState(null);
  const [email, setEmail] = useState("");
  const [displayForgotPassword, setDisplayForgotPassword] = useState(false);
  const toast = useRef(null);
  const loginService = new LoginService();
  const [loading, setLoading] = useState(false);

  const themeService = useMemo(() => new ThemeService(), []);
  const engineParams = new URLSearchParams(location.search);
  const usernameParam = engineParams.get("engine");
  const hasPublicTheme = Boolean(usernameParam && usernameParam !== "undefined");
  const [theme, setTheme] = useState(undefined);
  const [themeLoading, setThemeLoading] = useState(false);

  // Temp order
  const [currentVariation, setCurrentVariation] = useState(null);
  const [previewingProduct, setPreviewingProduct] = useState(null);
  const [productQty, setProductQty] = useState(1);
  const [productPercentExtracoin, setProductPercentExtracoin] = useState(0);

  const orderService = new OrderService();

  const handleRedict = () => {
    history.push("/specialcustomer", {
      username: usenameRefferal,
    });
  };

  const preloadImage = (src) =>
    new Promise((resolve) => {
      if (!src) {
        resolve();
        return;
      }
      const img = new Image();
      img.onload = () => resolve();
      img.onerror = () => resolve();
      img.src = src;
    });

  async function login(username, password) {
    if (username.length === 0 || password.length === 0) {
      toast.current.show({
        severity: "error",
        summary: localeText("error"),
        detail: localeText("pleaseFillOut"),
        life: 6000,
      });
      return;
    }

    if (tokenRecaptcha.length === 0) {
      toast.current.show({
        severity: "error",
        summary: localeText("error"),
        detail: localeText("pleaseReverify"),
        life: 6000,
      });
      return;
    }

    try {
      setLoading(true);
      const res = await loginService.postLogin(username, password);

      if (typeof res === "string" && res.trim() === "Too many login attempts") {
        // Bloqueio temporário: a senha nem foi conferida, então não é "credenciais incorretas"
        toast.current.show({
          severity: "error",
          summary: localeText("error"),
          detail: localeText("tooManyLoginAttempts"),
          life: 8000,
        });
        setLoading(false);
      } else if (res === "Bad credentials") {
        toast.current.show({
          severity: "error",
          summary: localeText("error"),
          detail: localeText("incorrectCredentials"),
          life: 6000,
        });
        setLoading(false);
      } else if (res === "User is disabled") {
        toast.current.show({
          severity: "error",
          summary: localeText("error"),
          detail: localeText("pleaseCheckEmail"),
          life: 6000,
        });
        setLoading(false);
      } else {
        localStorage.setItem("__extraconomy_token", res);

        try {
          const usernameResponse = await loginService.getUsername(res);

          let actualUsername = username;
          if (usernameResponse?.messageCode === 200001) {
            actualUsername = usernameResponse.result;
          } else if (usernameResponse?.result) {
            actualUsername = usernameResponse.result;
          } else if (usernameResponse?.username) {
            actualUsername = usernameResponse.username;
          }

          localStorage.setItem("__extraconomy_username", actualUsername);
        } catch (error) {
          console.error("Erro ao buscar username:", error);
          localStorage.setItem("__extraconomy_username", username);
        }

        if (
          localStorage.getItem("__extraconomy_lat__") &&
          localStorage.getItem("__extraconomy_ismobile__")
        ) {
          await loginService.postRegisterLocation(
            localStorage.getItem("__extraconomy_token"),
            localStorage.getItem("__extraconomy_lat__"),
            localStorage.getItem("__extraconomy_long__"),
            localStorage.getItem("__extraconomy_username"),
            localStorage.getItem("__extraconomy_precision__")
              ? localStorage.getItem("__extraconomy_precision__")
              : null,
          );
        }

        if (
          localStorage.getItem("__extraconomy_devicetoken__") &&
          localStorage.getItem("__extraconomy_ismobile__")
        ) {
          await loginService.postRegisterDevice(
            localStorage.getItem("__extraconomy_token"),
            localStorage.getItem("__extraconomy_devicetoken__"),
            localStorage.getItem("__extraconomy_platform__"),
            localStorage.getItem("__extraconomy_username"),
          );
        }

        if (
          localStorage.getItem("__extraconomy_token") &&
          JSON.parse(localStorage.getItem("__extraconomy_ismobile__"))
        ) {
          window.localStorage.setItem(
            "__extraconomy_token",
            localStorage.getItem("__extraconomy_token"),
          );
          window.localStorage.setItem(
            "__extraconomy_username",
            localStorage.getItem("__extraconomy_username"),
          );
          window.ReactNativeWebView.postMessage(
            JSON.stringify({
              token: localStorage.getItem("__extraconomy_token"),
              username: localStorage.getItem("__extraconomy_username"),
              isLogin: true,
            }),
          );
        }
        if (from) {
          history.replace(from, { from: location.pathname });
        } else if (localStorage.getItem("__extraconomy_temp_item")) {
          const productTemp = JSON.parse(localStorage.getItem("__extraconomy_temp_item"));

          addToCart(productTemp?.product?.product?.fringe);
        } else {
          await mergeGuestCart();
          window.location.replace("/dashboard");
        }
      }
    } catch (e) {
      setLoading(false);
      console.log(e);
    }
  }

  async function sendEmail(email) {
    try {
      setLoading(true);
      const res = await loginService.getForgotPassword(email);

      if (res.messageCode === 200000)
        toast.current.show({
          severity: "success",
          summary: localeText("success"),
          detail: localeText("pleaseCheckEmailTemp"),
        });
      else if (res.messageCode === 500012)
        toast.current.show({
          severity: "error",
          summary: localeText("error"),
          detail: localeText("userNotFound"),
        });
      else
        toast.current.show({
          severity: "error",
          summary: localeText("error"),
          detail: localeText("thisEmailDoesnt"),
        });

      setDisplayForgotPassword(false);
      setLoading(false);
    } catch (e) {
      setLoading(false);
      console.log(e);
    }
  }

  async function addToCart(isFringeBenefit) {
    try {
      const orderKey = isFringeBenefit
        ? "__extraconomy_order_fringe_benefit"
        : "__extraconomy_order";

      if (!localStorage.getItem(orderKey)) {
        let res;

        if (isFringeBenefit) {
          res = await orderService.postCreateFringeBenefitOrder(
            previewingProduct.product.id,
            productQty,
          );
        } else {
          res = await orderService.postCreateOrder(
            localStorage.getItem("__extraconomy_token"),
            localStorage.getItem("__extraconomy_username"),
            previewingProduct.product.user.username,
            previewingProduct.product.id,
            productQty,
            productPercentExtracoin,
            currentVariation ? currentVariation.id : null,
            shareToken,
          );
        }

        if (res.messageCode !== 200000) {
          toast.current.show({
            severity: "error",
            summary: localeText("error"),
            detail: localeText("data").find((msg) => msg.code === res.messageCode).message,
            life: 6000,
          });
          localStorage.removeItem("__extraconomy_temp_item");
          setLoading(false);
        } else {
          const order = res.result;
          localStorage.setItem(orderKey, JSON.stringify(order));
          localStorage.removeItem("__extraconomy_temp_item");
          const cartUrl = isFringeBenefit ? "/cart-fringe-benefit" : "/cart";
          window.location.href = cartUrl;
        }
      } else {
        addItem(orderKey, isFringeBenefit);
        localStorage.removeItem("__extraconomy_temp_item");
      }
    } catch (e) {
      if (e.status === 401) {
        localStorage.removeItem("__extraconomy_token");
        localStorage.removeItem("__extraconomy_username");
        window.location.href = "/login";
      }
      console.log(e);
    }
  }

  async function addItem(orderKey, isFringeBenefit) {
    try {
      const order = JSON.parse(localStorage.getItem(orderKey));

      const res = await orderService.postAddItem(
        localStorage.getItem("__extraconomy_token"),
        localStorage.getItem("__extraconomy_username"),
        order.id,
        previewingProduct.product.id,
        productQty,
        productPercentExtracoin,
        currentVariation ? currentVariation.id : null,
      );

      if (res.messageCode !== 200000) {
        toast.current.show({
          severity: "error",
          summary: localeText("error"),
          detail: localeText("data").find((msg) => msg.code === res.messageCode).message,
          life: 6000,
        });
      } else {
        const order = res.result;

        localStorage.setItem(orderKey, JSON.stringify(order));

        const cartUrl = isFringeBenefit ? "/cart-fringe-benefit" : "/cart";

        window.location.href = cartUrl;
      }
    } catch (e) {
      if (e.status === 401) {
        localStorage.removeItem("__extraconomy_token");
        localStorage.removeItem("__extraconomy_username");
        window.location.href = "/login";
      }
      console.log(e);
    }
  }

  async function mergeGuestCart() {
    try {
      const regularOrderKey = "__extraconomy_order";
      const guestOrder = localStorage.getItem(regularOrderKey);

      if (guestOrder) {
        const parsedGuestOrder = JSON.parse(guestOrder);

        if (parsedGuestOrder.isGuestCart && parsedGuestOrder.itens?.length > 0) {
          await createOrderFromGuestCart(parsedGuestOrder);
        }
      }
    } catch (e) {
      console.log("Error merging guest cart:", e);
    }
  }

  async function createOrderFromGuestCart(guestOrder) {
    try {
      const orderKey = "__extraconomy_order";
      const firstItem = guestOrder.itens[0];

      const res = await orderService.postCreateOrder(
        localStorage.getItem("__extraconomy_token"),
        localStorage.getItem("__extraconomy_username"),
        firstItem.product.user.username,
        firstItem.product.id,
        firstItem.quantity,
        0,
        firstItem.productVariation?.id || null,
        firstItem.shareToken,
      );

      if (res.messageCode === 200000) {
        let order = res.result;

        for (let i = 1; i < guestOrder.itens.length; i++) {
          const item = guestOrder.itens[i];
          const addRes = await orderService.postAddItem(
            localStorage.getItem("__extraconomy_token"),
            localStorage.getItem("__extraconomy_username"),
            order.id,
            item.product.id,
            item.quantity,
            0,
            item.productVariation?.id || null,
            item.shareToken,
          );

          if (addRes.messageCode === 200000) {
            order = addRes.result;
          }
        }

        localStorage.setItem(orderKey, JSON.stringify(order));
      } else {
        localStorage.removeItem(orderKey);
      }
    } catch (e) {
      console.log("Error creating order from guest cart:", e);
      localStorage.removeItem("__extraconomy_order");
    }
  }

  useEffect(() => {
    if (localStorage.getItem("__extraconomy_temp_item")) {
      const tempItem = JSON.parse(localStorage.getItem("__extraconomy_temp_item"));

      setPreviewingProduct(tempItem.product);
      setCurrentVariation(tempItem.variation);
      setProductQty(tempItem.qty);
      setProductPercentExtracoin(tempItem.percentExtracoin);
      setShareToken(tempItem.shareToken);
    }
  }, []);

  useEffect(() => {
    if (!hasPublicTheme) {
      setTheme(null);
      setThemeLoading(false);
      return undefined;
    }

    let cancelled = false;

    const loadTheme = async () => {
      try {
        setTheme(undefined);
        setThemeLoading(true);
        const res = await themeService.getThemeByUserPublic(usernameParam);
        const fetchedTheme = res?.result ?? null;
        const logoUrl = fetchedTheme?.urlLogo ? resolveThemeImageUrl(fetchedTheme.urlLogo) : null;

        await preloadImage(logoUrl);
        if (!cancelled) {
          setTheme(fetchedTheme);
        }
      } catch (err) {
        console.log("Error getting theme by user public:", err);
        if (!cancelled) {
          setTheme(null);
        }
      } finally {
        if (!cancelled) {
          setThemeLoading(false);
        }
      }
    };

    loadTheme();

    return () => {
      cancelled = true;
    };
  }, [hasPublicTheme, usernameParam, themeService]);

  const shouldDelayThemeRender = hasPublicTheme && (themeLoading || theme === undefined);

  return (
    <div className="flex ">
      <div className="login-container flex flex-column justify-content-between col">
        <SEO title="Login" description={localeText("loginSEODescription")} />
        <Toast position="top-left" ref={toast} />

        <Dialog
          header={localeText("forgotPassword")}
          visible={displayForgotPassword}
          onHide={() => setDisplayForgotPassword(false)}
          breakpoints={{ "960px": "75vw" }}
          style={{ width: "50vw" }}
          draggable={false}
        >
          <div className="text-center mb-4">
            <ul className="list-none text-left">
              <li>
                <label htmlFor="email">
                  {localeText("enterTheEmailAddressAssociatedYourAccount")}
                </label>
                <InputText
                  value={email}
                  name="email"
                  onChange={(e) => setEmail(e.target.value)}
                  type="email"
                  className="w-full mt-2"
                />
                <small className="text-xs text-gray-600">
                  {localeText("weWillSendYouALinkToSecurelyCreateANewPassword")}
                </small>
              </li>

              <li className="mt-4">
                <Button
                  label={localeText("sendEmail")}
                  loading={loading}
                  onClick={() => sendEmail(email)}
                />
              </li>
            </ul>
          </div>
        </Dialog>
        {/* <div className="login-panel"></div> */}

        <Link
          to={`${JSON.parse(localStorage.getItem("__extraconomy_ismobile__")) ? "/shop" : "/"}`}
          className="md:align-self-start align-self-center"
        >
          {shouldDelayThemeRender ? (
            <div style={{ height: "40px", width: "180px" }} />
          ) : theme?.urlLogo ? (
            <img src={resolveThemeImageUrl(theme.urlLogo)} alt="Logo" style={{ height: "40px" }} />
          ) : (
            <img
              src={`${process.env.PUBLIC_URL}/assets/layout/images/logo-color.png`}
              alt="Extraconomy Logo"
              style={{ height: "40px" }}
            />
          )}
        </Link>
        <div className="flex flex-column align-items-center width-100 gap-5">
          <div className="flex flex-column align-items-center gap-2">
            <div className="title text-4xl font-bold" style={{ color: "#181D27" }}>
              {localeText("welcomeBack")}
            </div>
            <div className="text-center" style={{ color: "#535862" }}>
              {localeText("signInWith")}
            </div>
          </div>
          <div className="flex flex-column gap-4 w-full" style={{ maxWidth: "360px" }}>
            <div className="w-full">
              <label className="font-semibold text-base" htmlFor="username">
                {localeText("emailOrUsername")}
              </label>
              <div className="login-input-wrapper mt-2">
                <InputText
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  name="username"
                  className="w-full p-3"
                  placeholder={localeText("enterRegisteredEmail")}
                />
              </div>
            </div>
            <div className="w-full">
              <label className="font-semibold text-base" htmlFor="username">
                {localeText("password")}
              </label>
              <div className="login-input-wrapper mt-2">
                <InputText
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  type="password"
                  name="password"
                  className="w-full p-3"
                  placeholder={localeText("enterYourPassword")}
                />
              </div>
            </div>

            <div className="flex justify-content-between">
              <div className="flex align-items-center gap-2">
                <Checkbox checked={rememberMe} onChange={(e) => setRememberMe(e.checked)} />
                <label htmlFor="rememberMe" className="text-sm" style={{ color: "#344563" }}>
                  {localeText("rememberMe")}
                </label>
              </div>
              <Button
                className="p-0 p-no-outline text-sm"
                style={{ color: "#344563" }}
                link
                onClick={() => setDisplayForgotPassword(true)}
              >
                {localeText("forgotPassword")}
              </Button>
            </div>

            <ReCaptchaButton token={tokenRecaptcha} setToken={setTokenRecaptcha} />

            <div>
              <Button
                label={localeText("accessMyAccount")}
                loading={loading}
                onClick={() => login(username, password)}
                style={{ backgroundColor: "#28AB69" }}
                className="w-full font-semibold text-base text-white p-no-outline border-none px-4 py-3 border-round-lg"
              />
            </div>
          </div>

          <div className="text-sm mb-0 text-center" style={{ padding: "4.2px", color: "#344563" }}>
            {localeText("notPartOfNetwork")}{" "}
            <span
              className="font-semibold"
              style={{ color: "#245AE5", cursor: "pointer" }}
              onClick={handleRedict}
            >
              {localeText("createAccountNow")}
            </span>
          </div>
        </div>
        <div>
          {!loading && (
            <div>
              {shouldDelayThemeRender ? (
                <p style={{ visibility: "hidden" }}>© Extraconomy {new Date().getFullYear()}</p>
              ) : theme?.subnetName ? (
                <p>
                  © {theme.subnetName} {new Date().getFullYear()}
                </p>
              ) : (
                <p>© Extraconomy {new Date().getFullYear()}</p>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="col p-0 secondary-login-container md:flex hidden">
        <div className="text-white w-full align-self-end secondary-login-container-text flex flex-column gap-5">
          <div>
            <img src={require("../images/pages/stars.png")} alt="stars" />
          </div>
          <div className="title font-bold" style={{ fontSize: "3.5rem", lineHeight: "1.2" }}>
            {localeText("loginAndKeepGrowing")}
          </div>
          <div className="text-lg" style={{ lineHeight: "1.5" }}>
            {localeText("accessYourAccountDescription")}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
